/**
 * A aba Decisões, portada de knowledge.js:437-510.
 *
 * Mesma linha do tempo do painel atual, com as duas regras que a sustentam: o
 * vínculo é o que faz a decisão aparecer na doc do arquivo que ela governa, e
 * por isso decisão sem vínculo recebe aviso em vez de silêncio; e o vínculo que
 * aponta para código que saiu do repositório fica marcado como deriva, porque a
 * decisão continua valendo mesmo quando o alvo dela mudou de nome.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { postar } from '../api/cliente';
import { chaves, useDecisoes, useDocs } from '../api/consultas';
import type { DecisaoCompleta } from '../api/tipos';
import { acionavel } from '../componentes/acionavel';
import { Carregando, Falha, Vazio } from '../componentes/Estado';
import { useRecado } from '../componentes/Recado';
import { ESPECIE_DE_DECISAO, rotulo } from '../dados/rotulos';

const classeDoTipo = (especie: string) => (especie === 'runbook' ? 'runbook' : especie === 'padrao' ? 'padrao' : '');

export function Decisoes({ slug }: { slug: string }) {
  const decisoes = useDecisoes(slug);
  const docs = useDocs(slug);
  const avisar = useRecado();
  const navegar = useNavigate();
  const [registrando, setRegistrando] = useState(false);

  if (decisoes.isPending) return <Carregando rotulo="as decisões do time" />;
  if (decisoes.error) {
    return <Falha erro={decisoes.error} rotulo="as decisões do time" aoRepetir={decisoes.refetch} />;
  }

  const lista = decisoes.data || [];
  const comDeriva = lista.filter((d) => d.drift).length;

  const abrirVinculo = (chave: string, ausente: boolean) => {
    if (ausente) { avisar('este alvo não existe mais no acervo nem no AST'); return; }
    if ((docs.data || []).some((d) => d.unit_key === chave)) {
      navegar(`/p/${slug}/doc?key=${encodeURIComponent(chave)}`);
      return;
    }
    // o que não está documentado pode existir só no AST: a aba do grafo já
    // aplica a busca que vem na URL e foca o nó
    navegar(`/p/${slug}/graph?no=${encodeURIComponent(chave)}`);
  };

  const abrirIntegra = (chave: string) => navegar(`/p/${slug}/doc?key=${encodeURIComponent(chave)}`);

  return (
    <>
      <div className="crumb"><b>{slug}</b><span className="sep">/</span>decisões</div>

      <div className="kb-head">
        <div>
          <div className="sec-title">Decisões, runbooks e padrões · {lista.length}</div>
          <div className="sec-sub">
            O porquê que o código não conta. Cada decisão aponta para os arquivos e nós que ela
            governa — e é marcada como <b>deriva</b> quando cita código que não existe mais. Entram
            na doc dos arquivos ligados e no pacote que o agente lê.
          </div>
        </div>
        <div className="kb-actions">
          <button type="button" className="btn" onClick={() => setRegistrando(true)}>Registrar decisão</button>
        </div>
      </div>

      {!!comDeriva && (
        <div className="warn-line">
          {comDeriva} decisão(ões) com deriva: o código citado mudou de nome ou saiu do repositório.
        </div>
      )}

      <div id="dec-form">
        {registrando && <FormularioDeDecisao slug={slug} aoFechar={() => setRegistrando(false)} />}
      </div>

      {lista.length ? (
        <div className="tl">
          {lista.map((d) => (
            <ItemDaDecisao
              key={d.unit_key}
              d={d}
              aoAbrirVinculo={abrirVinculo}
              aoAbrirIntegra={abrirIntegra}
            />
          ))}
        </div>
      ) : (
        <Vazio
          titulo="Nenhuma decisão registrada"
          acao={{ rotulo: 'Registrar a primeira', ao: () => setRegistrando(true) }}
        >
          Toda escolha de arquitetura que alguém vai questionar em seis meses cabe aqui: por que
          SQLite e não Redis, por que retry com backoff, por que este serviço não usa o gateway.
          Registrada, ela aparece automaticamente na doc dos arquivos que governa e no pacote que o
          agente lê.
        </Vazio>
      )}
    </>
  );
}

function ItemDaDecisao({ d, aoAbrirVinculo, aoAbrirIntegra }: {
  d: DecisaoCompleta;
  aoAbrirVinculo: (chave: string, ausente: boolean) => void;
  aoAbrirIntegra: (chave: string) => void;
}) {
  const vinculos = d.links || [];
  const ausentes = d.missing || [];

  return (
    <div className={`tl-item ${classeDoTipo(d.unit_kind)}`}>
      <div className="tl-card">
        <h4>
          <span className={`tag ${classeDoTipo(d.unit_kind) || 'decisao'}`}>
            {rotulo(ESPECIE_DE_DECISAO, d.unit_kind)}
          </span>
          {d.title} {d.drift && <span className="tag drift">deriva</span>}
        </h4>
        <div className="when">
          v{d.version} · {(d.generated_at || '').slice(0, 10)} · {d.generated_by || '—'}
        </div>
        <div className="sum">{d.summary || ''}</div>
        {vinculos.length ? (
          <div className="tl-links">
            {vinculos.map((l) => {
              const ausente = ausentes.includes(l);
              return (
                <span
                  key={l}
                  className={`lnk ${ausente ? 'miss' : ''}`}
                  {...acionavel(() => aoAbrirVinculo(l, ausente))}
                >
                  {l}
                </span>
              );
            })}
          </div>
        ) : (
          <div className="muted" style={{ fontSize: 12 }}>
            sem vínculo com código — ninguém vai ver esta decisão no arquivo que ela governa
          </div>
        )}
        <div className="prop-acts" style={{ marginTop: 10 }}>
          <button type="button" className="btn ghost sm" onClick={() => aoAbrirIntegra(d.unit_key)}>
            abrir íntegra
          </button>
        </div>
      </div>
    </div>
  );
}

/** O formulário de registro (knowledge.js:480-510), no lugar do `#dec-form`. */
function FormularioDeDecisao({ slug, aoFechar }: { slug: string; aoFechar: () => void }) {
  const avisar = useRecado();
  const fila = useQueryClient();
  const [titulo, setTitulo] = useState('');
  const [especie, setEspecie] = useState('decision');
  const [corpo, setCorpo] = useState('');
  const [vinculos, setVinculos] = useState('');

  const publicar = useMutation({
    mutationFn: (dados: { title: string; body_md: string; unit_kind: string; links: string[] }) =>
      postar(`/api/${slug}/decisions`, dados),
  });

  const enviar = async () => {
    if (!titulo.trim() || !corpo.trim()) { avisar('título e corpo obrigatórios'); return; }
    try {
      await publicar.mutateAsync({
        title: titulo.trim(),
        body_md: corpo.trim(),
        unit_kind: especie,
        links: vinculos.split(',').map((s) => s.trim()).filter(Boolean),
      });
      avisar('decisão publicada — já aparece na doc dos arquivos ligados');
      await fila.invalidateQueries({ queryKey: chaves.decisoes(slug) });
      aoFechar();
    } catch (e) {
      avisar(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="prop open pending" style={{ margin: '12px 0' }}>
      <div className="prop-head">
        <span className="tag decisao">nova</span>
        <span className="unit">registrar decisão</span>
      </div>
      <div className="prop-body">
        <div className="own-grid" style={{ gridTemplateColumns: '2fr 1fr' }}>
          <input
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            placeholder="título — ex: fila em SQLite em vez de Redis"
          />
          <select
            value={especie}
            onChange={(e) => setEspecie(e.target.value)}
            aria-label="tipo da decisão"
          >
            <option value="decision">decisão (ADR)</option>
            <option value="runbook">runbook</option>
            <option value="padrao">padrão do time</option>
          </select>
        </div>
        <div className="sbs" style={{ gridTemplateColumns: '1fr' }}>
          <div className="col">
            <div className="col-t">contexto · decisão · consequências (markdown)</div>
            <textarea
              value={corpo}
              onChange={(e) => setCorpo(e.target.value)}
              style={{ minHeight: 220 }}
              aria-label="contexto · decisão · consequências"
              placeholder={'## Contexto\n...\n\n## Decisão\n...\n\n## Consequências\n...'}
            />
          </div>
        </div>
        <div className="prop-acts">
          <input
            value={vinculos}
            onChange={(e) => setVinculos(e.target.value)}
            placeholder="arquivos/nós que esta decisão governa, separados por vírgula (ex: src/queue.js, DispatchService)"
          />
          <button type="button" className="btn" disabled={publicar.isPending} onClick={() => void enviar()}>
            Publicar decisão
          </button>
          <button type="button" className="btn ghost" onClick={aoFechar}>cancelar</button>
        </div>
      </div>
    </div>
  );
}
